import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react'
import { Link } from 'react-router-dom'
import { Check, FileSpreadsheet, ListChecks, Mail, MessageSquare, QrCode } from 'lucide-react'
import { CEREMONY_TYPE_LABELS } from '@shared/types'
import { SECTION_LABELS } from '@shared/checklist-templates'

import { CordBraid, CordMark, CordSpine, useReveal } from '../../ui/Cord'
import { useSession } from '../../lib/session'
import { dinero, fechaLarga, limite } from '../../lib/format'

/* ===========================================================================
   La cara comercial de la plataforma. No es la página de una boda: es la
   página del producto, para una pareja que acaba de comprometerse.

   El cordón de tres dobleces recorre la página como columna vertebral y se
   traza al hacer scroll, con un nudo en cada sección. Todo lo demás se
   mantiene callado: una columna, alineado a la izquierda, reglas finas.
   =========================================================================== */

const PRECIO_PREMIUM = dinero(3500)
const FECHA_PILOTO = fechaLarga('2026-12-19')
const SECCIONES_GUIA = SECTION_LABELS.cristiana

function cx(...partes: Array<string | false | null | undefined>): string {
  return partes.filter(Boolean).join(' ')
}

// Enlaces con aspecto de botón: <Button> es un <button> y aquí navegamos.
const BOTON_BASE =
  'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors'
const BOTON_SOLIDO = `${BOTON_BASE} bg-tinta text-bruma hover:bg-tinta-suave`
const BOTON_BORDE = `${BOTON_BASE} border border-palo text-tinta hover:bg-palo/30`
const ENLACE_TEXTO = 'text-tinta underline-offset-4 transition-colors hover:text-laton hover:underline'

// ---------------------------------------------------------------------------
// Piezas compartidas con /precios
// ---------------------------------------------------------------------------

export function CabeceraMarketing() {
  const { user } = useSession()

  return (
    <header className="border-b border-palo/60">
      <div className="mx-auto flex max-w-5xl items-center justify-between gap-3 px-5 py-4 sm:px-8">
        <Link
          to="/plataforma"
          className="flex items-center gap-2.5"
          aria-label="Nuestra Boda, ir al inicio de la plataforma"
        >
          <CordMark className="h-3 w-7 shrink-0 text-laton" />
          <span className="font-display text-base text-tinta sm:text-lg">Nuestra Boda</span>
        </Link>

        <nav className="flex items-center gap-1.5 sm:gap-4">
          {user ? (
            <Link
              to="/panel"
              className={`${BOTON_SOLIDO} px-3 py-2 text-xs sm:px-4 sm:py-2.5 sm:text-sm`}
            >
              Ir a mi panel
            </Link>
          ) : (
            <>
              <Link
                to="/entrar"
                className="rounded-md px-2 py-2 text-xs text-tinta transition-colors hover:text-laton sm:text-sm"
              >
                Entrar
              </Link>
              <Link
                to="/plataforma/registro"
                className={`${BOTON_SOLIDO} px-3 py-2 text-xs sm:px-4 sm:py-2.5 sm:text-sm`}
              >
                Crear mi boda gratis
              </Link>
            </>
          )}
        </nav>
      </div>
    </header>
  )
}

export function PieMarketing() {
  const { user } = useSession()

  return (
    <footer className="mx-auto w-full max-w-5xl px-5 pb-8 pt-8 sm:px-8">
      <hr className="regla" />
      <div className="mt-5 flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2.5">
            <CordMark className="h-3 w-7 text-laton" />
            <span className="font-display text-base text-tinta">Nuestra Boda</span>
          </div>
          <p className="mt-2.5 max-w-xs text-xs leading-relaxed text-palo-hondo">
            Hecha en Ecuador. La primera boda de la plataforma es la de Vanessa y Lenin, el{' '}
            <span className="tabular">{FECHA_PILOTO}</span>. Aquí probamos todo antes de ofrecerlo.
          </p>
        </div>

        <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <Link to="/plataforma/precios" className={ENLACE_TEXTO}>
            Precios
          </Link>
          {user ? (
            <Link to="/panel" className={ENLACE_TEXTO}>
              Mi panel
            </Link>
          ) : (
            <Link to="/entrar" className={ENLACE_TEXTO}>
              Entrar
            </Link>
          )}
          <Link to="/plataforma/registro" className={ENLACE_TEXTO}>
            Crear mi boda
          </Link>
        </nav>
      </div>
    </footer>
  )
}

// ---------------------------------------------------------------------------
// El cordón: un nudo por sección, medido de verdad sobre la página
// ---------------------------------------------------------------------------

const IDS_SECCION = ['que-resuelve', 'guia', 'precios', 'crear']

/** Devuelve la posición 0..1 de cada sección dentro del cuerpo de la página. */
function useNudos(cuerpo: RefObject<HTMLDivElement | null>): number[] {
  const [nudos, setNudos] = useState<number[]>([])

  useEffect(() => {
    const medir = () => {
      const caja = cuerpo.current
      if (!caja) return

      const alto = caja.offsetHeight
      if (alto <= 0) return

      const base = caja.getBoundingClientRect().top + window.scrollY
      const posiciones: number[] = []
      for (const id of IDS_SECCION) {
        const nodo = document.getElementById(id)
        if (!nodo) continue
        const arriba = nodo.getBoundingClientRect().top + window.scrollY
        posiciones.push(Math.min(1, Math.max(0, (arriba - base) / alto)))
      }

      setNudos((previas) => {
        const iguales =
          previas.length === posiciones.length &&
          previas.every((v, i) => Math.abs(v - (posiciones[i] ?? 0)) < 0.002)
        return iguales ? previas : posiciones
      })
    }

    medir()
    // El texto reflowea al cargar; volvemos a medir una vez ya pintado.
    const reintento = window.setTimeout(medir, 350)
    window.addEventListener('resize', medir)
    return () => {
      window.clearTimeout(reintento)
      window.removeEventListener('resize', medir)
    }
  }, [cuerpo])

  return nudos
}

// ---------------------------------------------------------------------------
// Bloques de la página
// ---------------------------------------------------------------------------

function Seccion({
  id,
  className,
  children,
}: {
  id: string
  className?: string
  children: ReactNode
}) {
  const ref = useReveal<HTMLElement>()
  return (
    <section id={id} ref={ref} className={cx('revelar', className)}>
      {children}
    </section>
  )
}

interface Capacidad {
  icono: typeof Check
  titulo: string
  texto: string
}

const CAPACIDADES: Capacidad[] = [
  {
    icono: Check,
    titulo: 'Confirmaciones sin que nadie descargue una app',
    texto:
      'Tu invitado abre el link, busca su nombre, dice si viene y con cuántos acompañantes. Sin cuenta, sin contraseña, sin instalar nada. Tú ves el conteo cambiar en tu panel, y los dos novios entran al mismo panel cada uno con su sesión.',
  },
  {
    icono: QrCode,
    titulo: 'Fotos que suben los invitados con un QR',
    texto:
      'Dejas el código QR en las mesas. Cada quien sube desde su celular las fotos que tomó, con su nombre. Tú decides cuáles se publican antes de que aparezcan en la galería.',
  },
  {
    icono: MessageSquare,
    titulo: 'Un libro de mensajes que no se pierde',
    texto:
      'Quien no alcanzó a hablarte en la fiesta te escribe ahí. Los mensajes pasan por tu aprobación y quedan guardados junto a las fotos, para leerlos cuando quieras.',
  },
  {
    icono: ListChecks,
    titulo: 'Una guía de planificación que no deja escapar ningún detalle',
    texto:
      'Una lista por etapas con lo que suele olvidarse: cuántas sesiones de consejería pide la iglesia, el turno en el Registro Civil, el cronograma hora por hora del día.',
  },
  {
    icono: Mail,
    titulo: 'Correos que salen solos',
    texto:
      'Reserva la fecha, invitación con botón de confirmar, recordatorio a quienes no responden, recordatorio final con la logística y agradecimiento con el link a las fotos. Editas el texto y eliges cuántos días antes sale cada uno.',
  },
]

const PARTES_GUIA: Array<{ titulo: string; texto: string }> = [
  {
    titulo: SECCIONES_GUIA.espiritual,
    texto:
      'Consejería prematrimonial, pasaje bíblico central, estructura de la ceremonia, la ceremonia de unidad —cordón de tres dobleces, arras, veladora—, los padrinos de anillos, arras, Biblia y lazo, y el ensayo general.',
  },
  {
    titulo: SECCIONES_GUIA.legal,
    texto:
      'Matrimonio civil en el Registro Civil: cédulas, dos testigos y turno agendado con anticipación. Y verificar si tu iglesia tiene convenio para que el matrimonio eclesiástico valga civilmente, o si son dos trámites separados.',
  },
  {
    titulo: SECCIONES_GUIA.logistica,
    texto:
      'Presupuesto por categoría, proveedores, línea de tiempo desde los 12 meses hasta la semana de la boda, y el cronograma hora por hora del día.',
  },
]

// ---------------------------------------------------------------------------

export default function Landing() {
  const cuerpo = useRef<HTMLDivElement>(null)
  const nudos = useNudos(cuerpo)

  useEffect(() => {
    document.title = 'Nuestra Boda — la página de tu boda, en un solo link'
  }, [])

  return (
    <div className="min-h-dvh bg-bruma">
      <CabeceraMarketing />

      <div ref={cuerpo} className="relative mx-auto w-full max-w-5xl px-5 sm:px-8">
        {/* La columna vertebral: se traza sola con el scroll de la página. */}
        <CordSpine
          knots={nudos}
          className="pointer-events-none absolute inset-y-0 left-1 h-full w-3.5 text-laton/70 sm:left-3"
        />

        <main className="pl-7 sm:pl-12">
          {/* --- Portada -------------------------------------------------- */}
          <section className="pb-10 pt-10 sm:pb-14 sm:pt-14">
            <p className="eyebrow">Para parejas que se acaban de comprometer</p>
            <h1 className="mt-3 font-display text-titulo text-tinta">
              Tu boda tiene mil detalles.
              <span className="mt-1 block text-palo-hondo">No se te va a escapar ninguno.</span>
            </h1>

            <p className="mt-5 max-w-[46ch] text-base leading-relaxed text-tinta/85">
              Creas la página de tu boda y compartes un solo link por WhatsApp. Tus invitados
              confirman desde el celular, suben las fotos que tomaron y te dejan un mensaje. Tú
              sigues una guía de planificación que va desde la consejería prematrimonial hasta el
              turno en el Registro Civil.
            </p>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to="/plataforma/registro" className={`${BOTON_SOLIDO} px-5 py-3 text-sm`}>
                Crear mi boda gratis
              </Link>
              <Link to="/plataforma/precios" className={`${BOTON_BORDE} px-5 py-3 text-sm`}>
                Ver los planes
              </Link>
            </div>

            <p className="mt-3 text-xs leading-relaxed text-palo-hondo">
              Gratis hasta 50 invitados y 100 fotos. Premium se paga una sola vez:{' '}
              <span className="tabular font-mono text-tinta">{PRECIO_PREMIUM}</span>. No hay
              mensualidad.
            </p>
          </section>

          {/* --- La tesis: el cordón -------------------------------------- */}
          <Seccion id="cordon" className="border-t border-palo/60 py-10 sm:py-12">
            <p className="eyebrow">Por qué un cordón</p>
            <div className="mt-4 sm:grid sm:grid-cols-[minmax(0,20rem)_1fr] sm:gap-10">
              <div>
                <blockquote className="max-w-[38ch] font-display text-2xl leading-snug text-tinta">
                  «Cordón de tres dobleces no se rompe pronto».
                </blockquote>
                <p className="mt-2.5 text-xs text-palo-hondo">Eclesiastés 4:12</p>
              </div>
              <p className="mt-5 max-w-[52ch] text-sm leading-relaxed text-tinta/85 sm:mt-1">
                Tres hebras: ustedes dos y Dios. Es el rito que amarra la ceremonia cristiana
                latinoamericana, y es la línea que baja por esta página con un nudo en cada sección.
                Dentro de tu panel, esas mismas tres hebras son tu avance real: una por cada parte de
                la guía de planificación.
              </p>
            </div>
          </Seccion>

          {/* --- Qué resuelve --------------------------------------------- */}
          <Seccion id="que-resuelve" className="border-t border-palo/60 py-10 sm:py-12">
            <p className="eyebrow">Qué resuelve</p>
            <h2 className="mt-3 max-w-[24ch] font-display text-titulo text-tinta">
              Hoy todo esto vive en cinco chats distintos. Aquí vive en un link.
            </h2>

            {/* Rejilla, no pila: cinco capacidades y la nota operativa caben en
                tres filas de dos, con el borde superior como único separador. */}
            <ul className="mt-6 grid gap-x-10 gap-y-6 sm:grid-cols-2">
              {CAPACIDADES.map(({ icono: Icono, titulo, texto }) => (
                <li key={titulo} className="border-t border-palo/60 pt-5">
                  <div className="flex items-start gap-3">
                    <Icono aria-hidden="true" className="mt-1 size-4 shrink-0 text-laton" />
                    <div>
                      <h3 className="font-display text-lg leading-snug text-tinta">{titulo}</h3>
                      <p className="mt-2 max-w-[54ch] text-sm leading-relaxed text-palo-hondo">
                        {texto}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
              <li className="border-t border-palo/60 pt-5">
                <div className="flex items-start gap-3">
                  <FileSpreadsheet aria-hidden="true" className="mt-1 size-4 shrink-0 text-laton" />
                  <div>
                    <h3 className="font-display text-lg leading-snug text-tinta">
                      Y los detalles operativos
                    </h3>
                    <p className="mt-2 max-w-[54ch] text-sm leading-relaxed text-palo-hondo">
                      Además: carga masiva de invitados desde Excel o CSV, aviso cuando un nombre
                      parece repetido, notas por invitado (alergias, hospedaje) y la lista completa
                      exportable cuando la necesites.
                    </p>
                  </div>
                </div>
              </li>
            </ul>
          </Seccion>

          {/* --- La guía cristiana: el diferencial ------------------------ */}
          <Seccion id="guia" className="border-t border-palo/60 py-10 sm:py-12">
            <p className="eyebrow">Lo que ninguna plantilla trae</p>
            <h2 className="mt-3 max-w-[26ch] font-display text-titulo text-tinta">
              La guía de planificación cristiana
            </h2>
            <p className="mt-5 max-w-[52ch] text-sm leading-relaxed text-tinta/85">
              Cualquier plantilla te da una página bonita. Ninguna te avisa que la consejería
              prematrimonial puede pedirse con seis meses de anticipación. Tu guía viene armada
              desde el primer día, en tres partes, y guarda el avance de los dos.
            </p>

            {/* Tres partes, tres columnas: la estructura ES el argumento. */}
            <dl className="mt-6 grid gap-6 sm:grid-cols-3">
              {PARTES_GUIA.map(({ titulo, texto }) => (
                <div key={titulo} className="border-l border-palo pl-5">
                  <dt className="font-display text-lg text-tinta">{titulo}</dt>
                  <dd className="mt-2 max-w-[52ch] text-sm leading-relaxed text-palo-hondo">
                    {texto}
                  </dd>
                </div>
              ))}
            </dl>

            <div className="mt-6 sm:flex sm:items-start sm:gap-10">
              <div className="sm:w-1/2">
                <CordBraid strands={[0.85, 1, 0.5]} className="h-5 w-full max-w-sm text-laton" />
                <p className="mt-3 max-w-[46ch] text-xs leading-relaxed text-palo-hondo">
                  Cada hebra es una parte de la guía. Se trenzan a medida que marcas lo hecho; el
                  cordón queda completo el día que terminan de planificar.
                </p>
              </div>
              <p className="mt-5 max-w-[52ch] text-sm leading-relaxed text-palo-hondo sm:mt-0 sm:w-1/2">
                Si tu ceremonia es {CEREMONY_TYPE_LABELS.catolica.toLowerCase()} o{' '}
                {CEREMONY_TYPE_LABELS.civil.toLowerCase()}, la guía cambia a esa plantilla. Y siempre
                puedes agregar tus propias tareas.
              </p>
            </div>
          </Seccion>

          {/* --- Precios en resumen --------------------------------------- */}
          <Seccion id="precios" className="border-t border-palo/60 py-10 sm:py-12">
            <p className="eyebrow">Precios</p>
            <h2 className="mt-3 max-w-[26ch] font-display text-titulo text-tinta">
              Gratis para empezar. Un solo pago si te queda chico.
            </h2>

            <table className="mt-6 w-full max-w-2xl border-collapse text-left text-sm">
              <caption className="sr-only">Comparación entre el plan gratis y el Premium</caption>
              <thead>
                <tr className="border-b border-palo">
                  <th scope="col" className="py-2.5 pr-3 font-normal text-palo-hondo">
                    <span className="eyebrow">Plan</span>
                  </th>
                  <th scope="col" className="py-2.5 pr-3 font-display text-base font-normal text-tinta">
                    Gratis
                  </th>
                  <th scope="col" className="py-2.5 font-display text-base font-normal text-tinta">
                    Premium
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-palo/60">
                <tr>
                  <th scope="row" className="py-2.5 pr-3 text-left font-normal text-palo-hondo">
                    Invitados
                  </th>
                  <td className="tabular py-2.5 pr-3 font-mono text-tinta">{limite(50)}</td>
                  <td className="tabular py-2.5 font-mono text-tinta">{limite(null)}</td>
                </tr>
                <tr>
                  <th scope="row" className="py-2.5 pr-3 text-left font-normal text-palo-hondo">
                    Fotos
                  </th>
                  <td className="tabular py-2.5 pr-3 font-mono text-tinta">{limite(100)}</td>
                  <td className="tabular py-2.5 font-mono text-tinta">{limite(null)}</td>
                </tr>
                <tr>
                  <th scope="row" className="py-2.5 pr-3 text-left font-normal text-palo-hondo">
                    Marca de la plataforma
                  </th>
                  <td className="py-2.5 pr-3 text-tinta">Al pie de tu página</td>
                  <td className="py-2.5 text-tinta">No aparece</td>
                </tr>
                <tr>
                  <th scope="row" className="py-2.5 pr-3 text-left font-normal text-palo-hondo">
                    Precio
                  </th>
                  <td className="tabular py-2.5 pr-3 font-mono text-tinta">{dinero(0)}</td>
                  <td className="py-2.5 text-tinta">
                    <span className="tabular font-mono">{PRECIO_PREMIUM}</span>{' '}
                    <span className="text-palo-hondo">pago único</span>
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="mt-5 max-w-[52ch] text-sm leading-relaxed text-palo-hondo">
              Premium no es una suscripción: una boda tiene fecha de cierre, no una mensualidad. Se
              paga una vez por esa boda y no se renueva.
            </p>

            <p className="mt-4 text-sm">
              <Link to="/plataforma/precios" className={ENLACE_TEXTO}>
                Ver todo lo que incluye cada plan
              </Link>
            </p>
          </Seccion>

          {/* --- Cierre --------------------------------------------------- */}
          <Seccion id="crear" className="border-t border-palo/60 py-10 sm:py-14">
            <div className="sm:flex sm:items-end sm:justify-between sm:gap-8">
              <div>
                <h2 className="max-w-[22ch] font-display text-titulo text-tinta">
                  Crea tu boda hoy y comparte el link mañana.
                </h2>
                <p className="mt-4 max-w-[46ch] text-sm leading-relaxed text-palo-hondo">
                  Necesitas los dos nombres y una fecha tentativa. Todo lo demás se edita después,
                  cuantas veces quieras: la lista de invitados nunca se cierra.
                </p>
              </div>
              <div className="mt-6 sm:mt-0 sm:shrink-0">
                <Link to="/plataforma/registro" className={`${BOTON_SOLIDO} px-5 py-3 text-sm`}>
                  Crear mi boda gratis
                </Link>
              </div>
            </div>
          </Seccion>
        </main>
      </div>

      <PieMarketing />
    </div>
  )
}
